import { Box, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useModal from '../../../hooks/useModal/useModal';
import { Banner, BannerStatus } from '../../../models/BannersModels';

import MenuItemWrapper from '../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../components/Menu/MenuStyle';

interface BannerRowOptionsProps {
  banner: Banner;
  cancelBanner: (bannerId: string) => void;
  deleteBanner: (bannerId: string) => void;
}

function BannerRowOptions({ banner, cancelBanner, deleteBanner }: BannerRowOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const modal = useModal();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handler for the edit banner action
   */
  const editBanner = () => {
    handleClose();
    navigate(`/banners/edit/${banner.id}`);
  };

  /**
   * Displays the cancel confirmation modal
   */
  const showCancelConfirmationModal = () => {
    handleClose();
    modal.showModal({
      modalTitle: t('pages.banners.cancelBanner.title'),
      modalDescription: t('pages.banners.cancelBanner.message', {
        name: banner.bannerTitle,
        interpolation: { escapeValue: false },
      }),
      confirmationModal: true,
      confirmationButtonLabel: t('pages.banners.cancelBanner.confirmLabel'),
      cancelButtonLabel: t('pages.banners.cancelBanner.discardLabel'),
      onAccept: (setOpen) => {
        setOpen(false);
        cancelBanner(banner.id);
      },
    });
  };

  /**
   * Displays the delete confirmation modal
   */
  const showDeleteConfirmationModal = () => {
    handleClose();
    modal.showModal({
      modalTitle: t('pages.banners.deleteBanner.title'),
      modalDescription: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            {t('pages.banners.deleteBanner.message1', {
              name: banner.bannerTitle,
              interpolation: { escapeValue: false },
            })}
          </span>
          <span>{t('pages.banners.deleteBanner.message2')}</span>
        </Box>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('pages.banners.deleteBanner.confirmLabel'),
      cancelButtonLabel: t('common.cancel'),
      onAccept: (setOpen) => {
        setOpen(false);
        deleteBanner(banner.id);
      },
    });
  };

  /**
   * Render cancel option
   */
  const renderCancelOption = () => {
    if (banner.status === BannerStatus.SCHEDULED || banner.status === BannerStatus.ACTIVE) {
      return (
        <MenuItemWrapper onClick={showCancelConfirmationModal}>
          {t('pages.banners.table.options.cancel')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render delete option
   */
  const renderDeleteOption = () => {
    if (banner.status !== BannerStatus.ACTIVE) {
      return (
        <MenuItemWrapper onClick={showDeleteConfirmationModal} sx={{ color: theme.palette.primary.main }}>
          {t('pages.banners.table.options.delete')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  return (
    <>
      <Box
        id={`box-${banner.id}`}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '0.375rem',
          width: '2.25rem',
          height: '2.25rem',
          flexShrink: 0,
          ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
        }}
        onClick={handleClick}
      >
        <VerticalDotsOutlineIcon />
      </Box>
      <Box>
        <MenuWrapper
          id={`menu-${banner.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItemWrapper onClick={editBanner}>{t('pages.banners.table.options.edit')}</MenuItemWrapper>
          {renderCancelOption()}
          {renderDeleteOption()}
        </MenuWrapper>
      </Box>
    </>
  );
}

export default BannerRowOptions;
